import { Fragment, useEffect, useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import { Card, CardContent, CardHeader } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { SCREENS, ROLES, type PermissionLevel, type AppRole } from '@wsfull/shared'

type Matrix = Record<string, Record<string, PermissionLevel>>
type MatrixResp = { roles: typeof ROLES; matrix: Matrix }

const LEVELS: { value: PermissionLevel; label: string }[] = [
  { value: 'none', label: 'Sem acesso' },
  { value: 'read', label: 'Leitura' },
  { value: 'edit', label: 'Edição' },
]
const cellClass: Record<PermissionLevel, string> = {
  none: 'bg-gray-50 text-gray-400',
  read: 'bg-amber-50 text-amber-800',
  edit: 'bg-emerald-50 text-emerald-800',
}

// Agrupa as telas mantendo a ordem do registro.
const GROUPS = SCREENS.reduce<{ group: string; keys: typeof SCREENS }[]>((acc, s) => {
  const last = acc[acc.length - 1]
  if (last && last.group === s.group) last.keys.push(s)
  else acc.push({ group: s.group, keys: [s] })
  return acc
}, [])

export function PermissionsPage() {
  const qc = useQueryClient()
  const { data, isLoading } = useQuery<MatrixResp>({
    queryKey: ['permissions', 'matrix'],
    queryFn: async () => (await api.get('/permissions')).data,
  })

  const [draft, setDraft] = useState<Matrix>({})
  const [msg, setMsg] = useState<string | null>(null)
  useEffect(() => { if (data?.matrix) setDraft(structuredClone(data.matrix)) }, [data])

  const dirty = useMemo(() => JSON.stringify(draft) !== JSON.stringify(data?.matrix ?? {}), [draft, data])

  const save = useMutation({
    mutationFn: async () => {
      const entries: { role: string; screenKey: string; level: PermissionLevel }[] = []
      for (const { key: role } of ROLES) {
        if (role === 'admin') continue
        for (const s of SCREENS) entries.push({ role, screenKey: s.key, level: draft[role]?.[s.key] ?? 'none' })
      }
      return (await api.put('/permissions', { entries })).data as MatrixResp
    },
    onSuccess: (resp) => {
      setDraft(structuredClone(resp.matrix))
      qc.setQueryData(['permissions', 'matrix'], resp)
      qc.invalidateQueries({ queryKey: ['permissions', 'me'] })
      setMsg('Acessos salvos. Cada usuário vê a mudança ao recarregar / no próximo login.')
    },
  })

  const setCell = (role: string, key: string, level: PermissionLevel) =>
    setDraft(d => ({ ...d, [role]: { ...d[role], [key]: level } }))

  const setRoleColumn = (role: AppRole, level: PermissionLevel) =>
    setDraft(d => ({ ...d, [role]: Object.fromEntries(SCREENS.map(s => [s.key, level])) }))

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Controle de acessos</h2>
        <p className="text-sm text-gray-500">Libere cada tela por perfil — <b>Sem acesso</b> (nem aparece no menu), <b>Leitura</b> (vê, sem alterar) ou <b>Edição</b> (total).</p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="text-sm font-semibold">Telas × perfis</span>
            <div className="flex items-center gap-3">
              {msg && <span className="text-xs font-medium text-emerald-700">{msg}</span>}
              <Button loading={save.isPending} disabled={!dirty} onClick={() => { setMsg(null); save.mutate() }}>
                Salvar acessos
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <p className="px-5 py-10 text-center text-sm text-gray-400">Carregando…</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-gray-200 bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Tela</th>
                    {ROLES.map(r => (
                      <th key={r.key} className="px-3 py-2 text-center font-medium text-gray-600">
                        <div>{r.label}</div>
                        {r.key === 'admin' ? (
                          <div className="text-[11px] font-normal text-gray-400">total (fixo)</div>
                        ) : (
                          <select
                            className="mt-1 rounded border border-gray-200 bg-white px-1 py-0.5 text-[11px] font-normal text-gray-500"
                            value=""
                            onChange={e => { if (e.target.value) setRoleColumn(r.key as AppRole, e.target.value as PermissionLevel) }}
                            title="Aplicar a todas as telas"
                          >
                            <option value="">todas…</option>
                            {LEVELS.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                          </select>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {GROUPS.map(g => (
                    <Fragment key={g.group}>
                      <tr className="bg-gray-50/70">
                        <td colSpan={ROLES.length + 1} className="px-4 py-1.5 font-mono text-[11px] uppercase tracking-wide text-gray-400">{g.group}</td>
                      </tr>
                      {g.keys.map(s => (
                        <tr key={s.key} className="border-b border-gray-100">
                          <td className="px-4 py-2 text-gray-800">{s.label}</td>
                          {ROLES.map(r => {
                            const lvl = r.key === 'admin' ? 'edit' : draft[r.key]?.[s.key] ?? 'none'
                            return (
                              <td key={r.key} className="px-3 py-1.5 text-center">
                                {r.key === 'admin' ? (
                                  <span className="inline-block rounded px-2 py-1 text-xs font-medium bg-emerald-50 text-emerald-800">Edição</span>
                                ) : (
                                  <select
                                    value={lvl}
                                    onChange={e => setCell(r.key, s.key, e.target.value as PermissionLevel)}
                                    className={`rounded px-2 py-1 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 ${cellClass[lvl as PermissionLevel]}`}
                                  >
                                    {LEVELS.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                                  </select>
                                )}
                              </td>
                            )
                          })}
                        </tr>
                      ))}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <p className="text-xs text-gray-400">
        O perfil <b>Admin</b> tem acesso total e não pode ser reduzido (evita travar o sistema). As mudanças valem para todos os usuários daquele perfil.
      </p>
    </div>
  )
}
